import React, { useState, useEffect } from 'react'
import { isInstagramGraphApiEnabled } from '../../utils/featureFlags'
import {
  Search,
  Plus,
  Edit,
  Instagram,
  Music,
  Facebook,
  Twitter,
  Youtube,
  Linkedin,
  Link2,
  Unlink,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  BarChart2
} from 'lucide-react'
import { Card, Button, Badge } from '../ui'
import Tooltip from '../ui/Tooltip'
import { Organization } from '../../services/organizationService'
import { Profile, profileService } from '../../services/profileService'
import { customerService } from '../../services/customerService'
import { Customer } from '../../types/customer'
import OrganizationPlatformAccountDrawer from './OrganizationPlatformAccountDrawer'
import AccountDemographics from '../social-media/AccountDemographics'
import { useToast } from '../../contexts/ToastContext'
import usePermissionsStore from '../../stores/permissionsStore'
import useInstagramConnectionStore from '../../stores/instagramConnectionStore'
import { InstagramConnection } from '../../services/instagramConnectionService'

interface OrganizationAccountsProps {
  organization: Organization
  onRefresh?: () => void
}

const OrganizationAccounts: React.FC<OrganizationAccountsProps> = ({
  organization
  // onRefresh - commented out as it's not needed
}) => {
  const { showError, showSuccess } = useToast()
  const { loadPermissions, permissions, hasFeature } = usePermissionsStore()
  const {
    connections,
    loadConnections,
    startConnection,
    exchangeCode,
    disconnect,
    clearError,
    error: connectionError
  } = useInstagramConnectionStore()
  const [disconnecting, setDisconnecting] = useState<string | null>(null)
  const [confirmDisconnect, setConfirmDisconnect] = useState<InstagramConnection | null>(null)
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [loadingProfiles, setLoadingProfiles] = useState(true) // Start with loading true
  const [accountSearchTerm, setAccountSearchTerm] = useState('')
  const [expandedDemographicsProfileId, setExpandedDemographicsProfileId] = useState<string | null>(null)

  // Account drawer state
  const [showPlatformAccountDrawer, setShowPlatformAccountDrawer] =
    useState(false)
  const [editingPlatformAccount, setEditingPlatformAccount] =
    useState<any>(null)
  const [isCreatingAccount, setIsCreatingAccount] = useState(false)

  // Load permissions for this organization
  useEffect(() => {
    loadPermissions(organization.id)
  }, [organization.id, loadPermissions])

  // Load Instagram connections for this organization
  useEffect(() => {
    loadConnections(organization.id)
  }, [organization.id, loadConnections])

  // Handle OAuth callback — exchange code for tokens
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const state = params.get('state')
    const oauthCallback = params.get('oauth')
    const oauthResult = params.get('ig_connected')
    const oauthError = params.get('ig_error')

    if (oauthCallback === 'callback' && code && state) {
      // Exchange the Facebook OAuth code for tokens
      window.history.replaceState({}, '', window.location.pathname)
      exchangeCode(code, state, organization.id).then((success) => {
        if (success) {
          showSuccess('Instagram Connected', 'Your Instagram account has been connected successfully.')
        }
      })
    } else if (oauthResult === 'true') {
      showSuccess('Instagram Connected', 'Your Instagram account has been connected successfully.')
      window.history.replaceState({}, '', window.location.pathname)
      loadConnections(organization.id)
    } else if (oauthError) {
      showError('Connection Failed', decodeURIComponent(oauthError))
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [organization.id, loadConnections, exchangeCode, showSuccess, showError])

  // Show connection errors via toast
  useEffect(() => {
    if (connectionError) {
      showError('Connection Error', connectionError)
      clearError()
    }
  }, [connectionError, showError, clearError])

  // Load the customer for this organization
  useEffect(() => {
    const loadCustomer = async () => {
      try {
        const response = await customerService.getCustomer(
          organization.customerId
        )
        if (response.success) {
          setCustomer(response.data.customer)
        } else {
          setLoadingProfiles(false) // Stop loading if customer fetch fails
        }
      } catch (error) {
        console.error('Error loading customer:', error)
        showError(
          'Failed to load customer',
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred'
        )
        setLoadingProfiles(false) // Stop loading on error
      }
    }

    // Reset states when organization changes
    setCustomer(null)
    setProfiles([])
    setLoadingProfiles(true)

    loadCustomer()
  }, [organization.customerId, showError])

  // Load profiles for this organization's customer
  useEffect(() => {
    const fetchProfiles = async () => {
      if (!customer?.id) {
        return // Wait for customer to load first
      }

      try {
        setLoadingProfiles(true)

        const response = await profileService.getProfiles({
          customerId: customer.id,
          includeInactive: true
        })

        setProfiles(response.data.profiles)
      } catch (error) {
        showError(
          'Failed to load platform accounts',
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred'
        )
      } finally {
        setLoadingProfiles(false)
      }
    }

    fetchProfiles()
  }, [customer?.id, showError])

  // Remove platforms loading since we get platform info from profiles

  // Calculate active account metrics
  const activeAccountCount = profiles.filter((p) => p.isActive).length
  const maxAccounts = permissions?.limits.max_social_accounts || 0
  const canAddAccount = activeAccountCount < maxAccounts

  const handleAddAccount = () => {
    // Check if we can add more accounts
    if (!canAddAccount) {
      showError(
        'Account Limit Reached',
        `You have reached the maximum of ${maxAccounts} active social accounts for your subscription tier. Please disable an existing account or upgrade your plan to add more accounts.`
      )
      return
    }

    setEditingPlatformAccount(null)
    setIsCreatingAccount(true)
    setShowPlatformAccountDrawer(true)
  }

  const handleEditAccount = (profile: Profile) => {
    setEditingPlatformAccount({
      id: profile.id,
      platform: profile.platform.name,
      username: profile.username,
      displayName: profile.displayName,
      category: profile.categoryName,
      viewRate: parseFloat(profile.emvViewRate || '0'),
      likeRate: parseFloat(profile.emvLikeRate || '0'),
      commentRate: parseFloat(profile.emvCommentRate || '0'),
      shareRate: parseFloat(profile.emvShareRate || '0'),
      isActive: profile.isActive !== undefined ? profile.isActive : true
    })
    setIsCreatingAccount(false)
    setShowPlatformAccountDrawer(true)
  }

  const handleCloseDrawer = () => {
    setShowPlatformAccountDrawer(false)
    setEditingPlatformAccount(null)
    setIsCreatingAccount(false)
  }

  const handleAccountSaved = async () => {
    // Refresh profiles after save
    if (!customer) {
      return
    }

    try {
      const response = await profileService.getProfiles({
        customerId: customer.id,
        includeInactive: true
      })

      const updatedProfiles = response.data.profiles

      // Validate that we haven't exceeded the limit
      const updatedActiveCount = updatedProfiles.filter(
        (p) => p.isActive
      ).length
      const limit = permissions?.limits.max_social_accounts || 0

      if (updatedActiveCount > limit) {
        showError(
          'Account Limit Exceeded',
          `Cannot save: You have ${updatedActiveCount} active accounts but your limit is ${limit}. Please disable some accounts to stay within your limit.`
        )
        return
      }

      setProfiles(updatedProfiles)
      handleCloseDrawer()
      // Don't call onRefresh here - it causes the parent drawer to close
      // onRefresh?.()
    } catch (error) {
      showError(
        'Failed to refresh accounts',
        error instanceof Error ? error.message : 'An unexpected error occurred'
      )
    }
  }

  // Get platform icon (matching Posts page implementation)
  const getPlatformIcon = (platformName: string) => {
    const lowerPlatform = platformName.toLowerCase()
    switch (lowerPlatform) {
      case 'instagram':
        return Instagram
      case 'tiktok':
        return Music
      case 'facebook':
        return Facebook
      case 'twitter':
      case 'x':
        return Twitter
      case 'youtube':
        return Youtube
      case 'linkedin':
        return Linkedin
      default:
        return Instagram
    }
  }

  // Get platform color (matching Posts page implementation)
  const getPlatformColor = (platformName: string) => {
    const lowerPlatform = platformName.toLowerCase()
    switch (lowerPlatform) {
      case 'instagram':
        return 'text-pink-500'
      case 'tiktok':
        return 'text-black'
      case 'facebook':
        return 'text-blue-600'
      case 'twitter':
      case 'x':
        return 'text-blue-400'
      case 'youtube':
        return 'text-red-600'
      case 'linkedin':
        return 'text-blue-700'
      default:
        return 'text-gray-500'
    }
  }

  // Filter profiles based on search term
  const filteredProfiles = profiles.filter(
    (profile) =>
      profile.username
        .toLowerCase()
        .includes(accountSearchTerm.toLowerCase()) ||
      profile.displayName
        ?.toLowerCase()
        .includes(accountSearchTerm.toLowerCase()) ||
      profile.platform.name
        .toLowerCase()
        .includes(accountSearchTerm.toLowerCase())
  )

  const formatNumber = (num: number | undefined | null) => {
    if (!num || num === 0) return '0'
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toString()
  }

  // Instagram Graph API feature gate — PostHog deploy flag AND org-level permission
  const isInstagramFeatureEnabled = isInstagramGraphApiEnabled() && !!permissions && hasFeature('instagram_graph_api')

  // Find matching Instagram connection for a profile
  const getConnectionForProfile = (profile: Profile): InstagramConnection | undefined => {
    if (!isInstagramFeatureEnabled) return undefined
    if (profile.platform.name.toLowerCase() !== 'instagram') return undefined
    return connections.find(
      (c) => c.linkedPlatformAccountId === profile.id || c.providerUsername === profile.username
    )
  }

  const handleConnect = async (profile: Profile) => {
    if (!customer?.id) return
    await startConnection(
      organization.id,
      customer.id,
      profile.id,
      profile.partitionKey
    )
  }

  const handleDisconnectClick = (connection: InstagramConnection) => {
    setConfirmDisconnect(connection)
  }

  const handleDisconnectConfirm = async () => {
    if (!confirmDisconnect) return
    const connection = confirmDisconnect
    setConfirmDisconnect(null)
    setDisconnecting(connection.id)
    try {
      await disconnect(connection.id, organization.id)
      showSuccess('Disconnected', `@${connection.providerUsername} has been disconnected.`)
    } catch {
      showError('Disconnect Failed', 'Failed to disconnect the account. Please try again.')
    } finally {
      setDisconnecting(null)
    }
  }

  const getConnectionStatusBadge = (status: InstagramConnection['tokenStatus']) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700 border border-green-200">
            <CheckCircle className="h-2.5 w-2.5" />
            Connected
          </span>
        )
      case 'expiring_soon':
        return (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">
            <Clock className="h-2.5 w-2.5" />
            Expiring
          </span>
        )
      case 'expired':
        return (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-red-50 text-red-700 border border-red-200">
            <AlertCircle className="h-2.5 w-2.5" />
            Expired
          </span>
        )
    }
  }

  // Human-readable token expiry for the account card (null for expired — the badge covers that)
  const formatTokenExpiry = (connection: InstagramConnection): string | null => {
    if (!connection.tokenExpiresAt || connection.tokenStatus === 'expired') return null
    const expires = new Date(connection.tokenExpiresAt)
    const days = Math.max(0, Math.ceil((expires.getTime() - Date.now()) / 86400000))
    if (days <= 14) return `Expires in ${days} day${days === 1 ? '' : 's'}`
    return `Expires ${expires.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
  }

  return (
    <div>
      <div className='space-y-6'>
        {/* Header */}
        <div className='flex items-center justify-between'>
          <div>
            <h3 className='text-lg font-medium text-gray-900'>
              Social Media Accounts
            </h3>
            <p className='text-sm text-gray-600'>
              {activeAccountCount} active / {maxAccounts} max •{' '}
              {profiles.length} total account{profiles.length !== 1 ? 's' : ''}{' '}
              for {customer?.customerName || 'this organization'}
            </p>
            {!canAddAccount && (
              <p className='text-sm text-amber-600 mt-1'>
                Account limit reached. Disable an account or upgrade your plan
                to add more.
              </p>
            )}
          </div>
          <Button
            size='xs'
            onClick={handleAddAccount}
            disabled={!canAddAccount}
            title={
              !canAddAccount
                ? `Account limit reached (${maxAccounts} max)`
                : 'Add a new social media account'
            }
          >
            <Plus className='h-3.5 w-3.5 mr-1.5' />
            Add Account
          </Button>
        </div>

        {/* Search */}
        <div className='relative'>
          <Search className='h-4 w-4 absolute left-3 top-3 text-gray-400' />
          <input
            type='text'
            placeholder='Search accounts by username, customer, or platform...'
            value={accountSearchTerm}
            onChange={(e) => setAccountSearchTerm(e.target.value)}
            className='w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-avi-primary focus:border-transparent'
          />
        </div>

        {/* Content Area */}
        {loadingProfiles ? (
          /* Loading State */
          <div className='flex items-center justify-center py-8'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-avi-primary'></div>
          </div>
        ) : filteredProfiles.length > 0 ? (
          /* Accounts Grid */
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
            {filteredProfiles.map((profile) => (
              <Card key={profile.id} variant='outlined' className='p-4 bg-gradient-to-br from-white to-slate-50/60 hover:shadow-md hover:border-indigo-200 transition-all duration-300'>
                <div className='flex items-start gap-3 mb-3'>
                  <div className='flex-shrink-0 p-2 bg-gradient-to-br from-gray-50 to-gray-200 rounded-lg shadow-sm'>
                    {profile.platform.name.toLowerCase() === 'tiktok' ? (
                      <img
                        src='/tiktok-logo.png'
                        alt='TikTok'
                        className='h-5 w-5 rounded-sm'
                      />
                    ) : (
                      (() => {
                        const PlatformIcon = getPlatformIcon(
                          profile.platform.name
                        )
                        const platformColor = getPlatformColor(
                          profile.platform.name
                        )
                        return (
                          <PlatformIcon
                            className={`h-5 w-5 ${platformColor}`}
                          />
                        )
                      })()
                    )}
                  </div>
                  <div className='flex-1 min-w-0'>
                    <h4 className='font-medium text-gray-900 flex items-center gap-1.5 flex-wrap'>
                      <span className='truncate'>@{profile.username}</span>
                      {profile.verified && (
                        <Badge variant='info' className='text-xs flex-shrink-0'>
                          ✓ Verified
                        </Badge>
                      )}
                    </h4>
                    <p className='text-sm text-gray-600 truncate'>
                      {profile.displayName || profile.username}
                    </p>
                    <p className='text-xs text-gray-500'>{profile.platform.name} • {profile.categoryName}</p>
                  </div>
                  <div className='flex-shrink-0 self-start'>
                    <Badge variant={profile.isActive ? 'success' : 'error'}>
                      {profile.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>

                <div className='grid grid-cols-3 gap-3 sm:gap-4 text-xs text-gray-600 mb-3'>
                  <div>
                    <span className='font-medium block'>Followers:</span>
                    <span className='text-gray-900'>
                      {formatNumber(profile.followerCount)}
                    </span>
                  </div>
                  <div>
                    <span className='font-medium block'>Following:</span>
                    <span className='text-gray-900'>
                      {formatNumber(profile.followingCount)}
                    </span>
                  </div>
                  <div>
                    <span className='font-medium block'>Posts:</span>
                    <span className='text-gray-900'>
                      {formatNumber(profile.videoCount)}
                    </span>
                  </div>
                </div>

                <div className='flex items-center justify-end gap-2'>
                    {(() => {
                      const connection = getConnectionForProfile(profile)
                      if (!isInstagramFeatureEnabled || profile.platform.name.toLowerCase() !== 'instagram') {
                        return null
                      }
                      if (!connection) {
                        return (
                          <Tooltip
                            content='Connect this account via Instagram Graph API to unlock views, shares, saves, and audience demographics — metrics not available through standard scraping.'
                            placement='top'
                            maxWidth={260}
                          >
                            <Button
                              variant='outline'
                              size='xs'
                              onClick={() => handleConnect(profile)}
                              className='text-blue-600 border-blue-200 hover:bg-blue-50'
                            >
                              <Link2 className='h-3 w-3 mr-1' />
                              Enable Enhanced API
                            </Button>
                          </Tooltip>
                        )
                      }
                      return (
                        <>
                          {formatTokenExpiry(connection) && (
                            <span className={`text-xs ${
                              connection.tokenStatus === 'expiring_soon' ? 'text-amber-600' : 'text-gray-500'
                            }`}>
                              {formatTokenExpiry(connection)}
                            </span>
                          )}
                          {connection.tokenStatus !== 'active' && getConnectionStatusBadge(connection.tokenStatus)}
                          {(connection.tokenStatus === 'expired' || connection.tokenStatus === 'expiring_soon') ? (
                            <Button
                              variant='outline'
                              size='xs'
                              onClick={() => handleConnect(profile)}
                              className='text-yellow-700 border-yellow-200 hover:bg-yellow-50'
                            >
                              <ExternalLink className='h-3 w-3 mr-1' />
                              Reconnect
                            </Button>
                          ) : (
                            <Button
                              variant='outline'
                              size='xs'
                              onClick={() => handleDisconnectClick(connection)}
                              disabled={disconnecting === connection.id}
                              className='text-red-600 border-red-200 hover:bg-red-50'
                            >
                              {disconnecting === connection.id ? (
                                <Loader2 className='h-3 w-3 animate-spin' />
                              ) : (
                                <>
                                  <Unlink className='h-3 w-3 mr-1' />
                                  Disconnect
                                </>
                              )}
                            </Button>
                          )}
                        </>
                      )
                    })()}
                    <Button
                      variant='outline'
                      size='xs'
                      onClick={() => handleEditAccount(profile)}
                    >
                      <Edit className='h-3 w-3 mr-1' />
                      Edit
                    </Button>
                </div>

                {/* Demographics toggle — only for connected active Instagram accounts */}
                {(() => {
                  const connection = getConnectionForProfile(profile)
                  if (!connection || connection.tokenStatus !== 'active') return null
                  const isExpanded = expandedDemographicsProfileId === profile.id
                  return (
                    <>
                      <div className='mt-3 pt-3 border-t border-gray-100'>
                        <button
                          onClick={() => setExpandedDemographicsProfileId(isExpanded ? null : profile.id)}
                          className='flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-800 font-medium transition-colors'
                        >
                          <BarChart2 className='h-3.5 w-3.5' />
                          Audience Demographics
                          {isExpanded ? <ChevronUp className='h-3.5 w-3.5' /> : <ChevronDown className='h-3.5 w-3.5' />}
                        </button>
                      </div>
                      {isExpanded && customer && (
                        <div className='mt-3'>
                          <AccountDemographics
                            customerId={customer.id}
                            platformAccountId={profile.id}
                            partitionKey={profile.partitionKey}
                          />
                        </div>
                      )}
                    </>
                  )
                })()}
              </Card>
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className='text-center py-12'>
            <div className='p-4 bg-gray-100 rounded-full w-16 h-16 mx-auto mb-4'>
              <Instagram className='h-8 w-8 text-gray-400' />
            </div>
            <h3 className='text-lg font-medium text-gray-900 mb-2'>
              {accountSearchTerm ? 'No accounts found' : 'No accounts'}
            </h3>
            <p className='text-gray-600 mb-4'>
              {accountSearchTerm
                ? 'Try adjusting your search criteria.'
                : 'Add social media accounts to start managing them.'}
            </p>
            {!accountSearchTerm && (
              <Button
                size='xs'
                onClick={handleAddAccount}
                disabled={!canAddAccount}
                title={
                  !canAddAccount
                    ? `Account limit reached (${maxAccounts} max)`
                    : 'Add your first social media account'
                }
              >
                <Plus className='h-3.5 w-3.5 mr-1.5' />
                Add First Account
              </Button>
            )}
          </div>
        )}

        {/* Platform Account Drawer */}
      </div>
      <OrganizationPlatformAccountDrawer
        isOpen={showPlatformAccountDrawer}
        onClose={handleCloseDrawer}
        platformAccount={editingPlatformAccount}
        isCreating={isCreatingAccount}
        customers={customer ? [customer] : []} // Pass the single customer as an array
        organization={organization}
        onSaved={handleAccountSaved}
        activeAccountCount={activeAccountCount}
        maxSocialAccounts={maxAccounts}
      />

      {/* Disconnect confirmation modal */}
      {confirmDisconnect && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'>
          <div className='bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4'>
            <div className='flex items-start gap-3 mb-4'>
              <div className='flex-shrink-0 h-9 w-9 rounded-full bg-red-100 flex items-center justify-center'>
                <Unlink className='h-4 w-4 text-red-600' />
              </div>
              <div>
                <h3 className='text-sm font-semibold text-gray-900'>Disconnect Enhanced API</h3>
                <p className='text-sm text-gray-600 mt-1'>
                  Are you sure you want to disconnect <span className='font-medium'>@{confirmDisconnect.providerUsername}</span>? Views, shares, saves, and audience demographics will no longer be enriched for this account.
                </p>
              </div>
            </div>
            <div className='flex justify-end gap-2'>
              <Button
                variant='outline'
                size='xs'
                onClick={() => setConfirmDisconnect(null)}
              >
                Cancel
              </Button>
              <Button
                size='xs'
                className='bg-red-600 hover:bg-red-700 text-white border-transparent'
                onClick={handleDisconnectConfirm}
              >
                Disconnect
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default OrganizationAccounts
